"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Layers, ShieldCheck, Droplets, Sparkles, SlidersHorizontal as Sliders, ArrowRight, CircleCheck as CheckCircle2, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyBenefits = [{
  id: 1,
  title: "Distribuição uniforme e precisa",
  description: "Controlo volumétrico rigoroso que elimina desperdícios de matéria-prima e evita sobrecargas na superfície.",
  iconName: "Sliders"
}, {
  id: 2,
  title: "Proteção contra desgaste e corrosão",
  description: "Camadas protetoras homogéneas que aumentam a vida útil de painéis, coberturas e peças metálicas.",
  iconName: "ShieldCheck"
}, {
  id: 3,
  title: "Eficiência de transferência superior",
  description: "Tecnologia de pulverização eletrostática e hidráulica com redução drástica do nevoeiro disperso.",
  iconName: "Droplets"
}, {
  id: 4,
  title: "Acabamento superficial consistente",
  description: "Aplicação contínua sem estrias ou falhas, garantindo a conformidade técnica dos produtos fabricados.",
  iconName: "Sparkles"
}];
const targetApplications = [{
  id: 1,
  title: "Revestimento de bobinas e perfis metálicos",
  detail: "Aplicação contínua de óleos protetores, passivantes e primários com bicos automáticos de corte rápido.",
  category: "Siderurgia & Metalurgia"
}, {
  id: 2,
  title: "Tratamento de painéis de madeira e cimento",
  detail: "Pulverização de selantes, resinas e agentes desmoldantes com controlo dinâmico de caudal.",
  category: "Construção & Painéis"
}, {
  id: 3,
  title: "Impermeabilização e proteção térmica",
  detail: "Sistemas de aspersão pressurizada para membranas protetoras e revestimentos anticorrosivos em coberturas.",
  category: "Infraestruturas"
}, {
  id: 4,
  title: "Linhas de pintura industrial e pré-tratamento",
  detail: "Bicos de precisão em aço inoxidável e cerâmica para lavagem química, fosfatação e enxaguamento uniforme.",
  category: "Manufatura Industrial"
}];
const technicalSpecs = [{
  id: 1,
  label: "Compatibilidade de produtos",
  val: "Líquidos viscosos, primários, óleos, vernizes e suspensões"
}, {
  id: 2,
  label: "Tecnologias de bico",
  val: "Atomização a ar, hidráulica e controlo elétrico de pulso (PSC)"
}, {
  id: 3,
  label: "Garantia de precisão",
  val: "Parceria técnica oficial Spraying Systems Co."
}, {
  id: 4,
  label: "Integração em linha",
  val: "Manifolds modulares adaptáveis a linhas automáticas"
}];
export default function Revestimentos() {
  const shouldReduceMotion = useReducedMotion();
  const getBenefitIcon = (name: string) => {
    switch (name) {
      case "Sliders":
        return <Sliders className="size-5 text-secondary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-secondary" />;
      case "Droplets":
        return <Droplets className="size-5 text-secondary" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-secondary" />;
      default:
        return <Layers className="size-5 text-secondary" />;
    }
  };
  return <section id="roofingcladdingoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium text-xs">
            Engenharia de Pulverização Industrial
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary tracking-tight mb-4">Porque o controlo do revestimento é importante ?</h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">O desempenho do revestimento depende de mais do que a simples seleção de um bico. Resultados uniformes exigem a combinação adequada de padrão de pulverização, caudal, design do cabeçote e estratégia de controlo. Aplicações de revestimento irregulares originam complicações evitáveis. Os fabricantes podem enfrentar desperdício, retrabalho, utilização excessiva de material e custos elevados, bem como qualidade inconsistente do produto</p>
        </div>

        {/* Hero Showcase Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16 md:mb-20">
          <motion.div className="lg:col-span-7 space-y-6" initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-foreground font-default">
                Controlo rigoroso em cada camada aplicada
              </h3>
              <p className="text-muted-foreground leading-relaxed">A tecnologia de pulverização adequada melhora a uniformidade da cobertura, reduz a sobrespray e mantém taxas de aplicação repetíveis à medida que as condições de produção se alteram. Quer se trate da aplicação de revestimentos leves atomizados, camadas funcionais mais pesadas, agentes de desmoldagem, lubrificantes, inibidores ou revestimentos alimentares como xaropes e glacês, soluções de revestimento bem adaptadas apoiam uma aplicação mais limpa, um melhor controlo do processo e um desempenho quotidiano mais fiável.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {technicalSpecs.map((spec, i) => <div key={spec.id} data-index={i} className="rounded-lg border border-border bg-card p-4 shadow-xs">
                  <p className="text-xs font-medium text-muted-foreground mb-1">{spec.label}</p>
                  <p className="text-sm font-semibold text-foreground">{spec.val}</p>
                </div>)}
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactsPage">
                  Solicitar estudo técnico
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent">
                <Link to="/CatalogsPage">
                  <FileText className="size-4 mr-1 text-muted-foreground" />
                  Ver catálogo de bicos
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-5" initial={shouldReduceMotion ? false : {
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <div className="relative rounded-2xl overflow-hidden border border-border bg-muted shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_coating_application.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_coating_application.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/industrial_coating_application.png-wvc-sizes" alt="Aplicação automatizada de revestimento em linha de produção industrial" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-primary/20 mix-blend-multiply pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 bg-primary/90 text-primary-foreground p-4 backdrop-blur-xs border-t border-primary/20">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-foreground/80">Eurocontrol Tecnologia</p>
                <p className="text-sm font-medium">Bicos automáticos com tecnologia de corte estanque instantâneo</p>
              </div>
            </div>
          </motion.div>
        </div>

        <Separator className="my-12 bg-border" />

        {/* Benefits Section */}
        <div className="mb-16 md:mb-20">
          <div className="max-w-2xl mb-8">
            <h3 className="text-2xl md:text-3xl font-bold font-default text-primary mb-2">
              Vantagens do sistema de revestimento Eurocontrol
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Maximização da rentabilidade e redução do consumo de produtos químicos em processos industriais contínuos.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyBenefits.map((benefit, i) => <motion.div key={benefit.id} data-index={i} initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.08,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-secondary/50">
                  <CardHeader className="pb-3">
                    <div className="size-10 rounded-lg bg-accent flex items-center justify-center mb-3">
                      {getBenefitIcon(benefit.iconName)}
                    </div>
                    <CardTitle className="text-base font-bold text-foreground font-default">
                      {benefit.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Applications Breakdown */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-10 shadow-sm">
          <div className="max-w-2xl mb-8">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="size-5 text-secondary" />
              <h3 className="text-xl md:text-2xl font-bold font-default text-primary">
                Aplicações e setores industriais
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Soluções aplicadas na indústria transformadora em Portugal com suporte e consultoria técnica especializada.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {targetApplications.map((app, i) => <div key={app.id} data-index={i} className="p-5 rounded-xl border border-border/80 bg-background/50 hover:bg-background transition-[background-color,border-color] duration-150">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-secondary shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs font-semibold text-secondary uppercase tracking-wider block mb-1">
                      {app.category}
                    </span>
                    <h4 className="text-base font-semibold text-foreground mb-1.5 font-default">
                      {app.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {app.detail}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              Necessita de testar o seu produto ou fluido com os nossos engenheiros?
            </div>
            <Button asChild size="default" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0">
              <Link to="/ContactsPage">
                Agendar demonstração técnica
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}